/**
 * Motor de calendario (§4.4) — **[NO NEGOCIABLE, es el diferenciador]**.
 *
 * El problema: compraste 12 sesiones que vencen el 12 de septiembre, el gimnasio cierra los
 * domingos, te vas de viaje el 2, y perdiste el jueves. ¿Te alcanza el tiempo?
 *
 * La respuesta que da este motor no es un sí/no: es un calendario concreto, un número de
 * días de colchón, y — cuando algo cambia — la frase que explica qué se movió y por qué.
 * Un recálculo que no se puede explicar es un recálculo en el que no se puede confiar.
 */

import { diffDias, rangoFechas } from './dates.ts';
import { UMBRAL_SOLAPAMIENTO, nombreMusculo, solapamientoEntreDias } from './musculos.ts';
import type {
  DiaBloqueado,
  Ejercicio,
  Fecha,
  Gimnasio,
  Musculo,
  Plan,
  RutinaDia,
  Sesion,
} from './types.ts';

/** §4.4 — alerta cuando el colchón baja de esto. */
export const UMBRAL_COLCHON_CRITICO = 2;

export interface ConflictoRecuperacion {
  conFecha: Fecha;
  musculos: Musculo[];
  score: number;
  /** §6.3 — la recomendación concreta, no un genérico "cuidado". */
  recomendacion: string;
}

export interface EntradaCalendario {
  fecha: Fecha;
  rutina_dia_id: string;
  nombre: string;
  /** Posición en la rotación del split (0-based). */
  indiceRotacion: number;
  /** Conflicto de recuperación con la sesión del día anterior, si no se pudo evitar. */
  conflicto: ConflictoRecuperacion | null;
  /**
   * true cuando la sesión ya existía como `planificada` y el motor la respetó en vez de
   * proponer otra cosa en esa fecha. El motor propone; no le pisa al usuario un día que ya
   * decidió. Sí le avisa si ese día encadena un músculo (el conflicto se calcula igual).
   */
  fijada: boolean;
}

export type MotivoNoDisponible = 'cierre_gimnasio' | 'bloqueado' | 'descanso_recuperacion' | 'ya_ocupado';

export interface DiaNoDisponible {
  fecha: Fecha;
  motivo: MotivoNoDisponible;
  detalle: string;
}

export interface AvisoCalendario {
  tipo: 'colchon_critico' | 'sesiones_en_riesgo' | 'conflicto_recuperacion' | 'plan_vencido';
  texto: string;
}

export interface PlanCalendario {
  desde: Fecha;
  hasta: Fecha;
  entradas: EntradaCalendario[];
  noDisponibles: DiaNoDisponible[];
  /** Días en que se puede entrenar entre hoy y el vencimiento. */
  diasHabiles: number;
  sesionesUsadas: number;
  sesionesPendientes: number;
  /** §4.4 — días hábiles disponibles menos sesiones pendientes. */
  colchon: number;
  colchonCritico: boolean;
  /** Sesiones que no caben antes del vencimiento: van a sobrar. */
  sesionesEnRiesgo: number;
  /** Último día en que se puede entrenar. Con un viaje al final, no es el vencimiento. */
  ultimaFechaUtil: Fecha | null;
  avisos: AvisoCalendario[];
}

export interface EntradaMotor {
  plan: Plan;
  gimnasio: Gimnasio;
  diasBloqueados: DiaBloqueado[];
  /** Los días del split, en su orden. */
  rutinaDias: RutinaDia[];
  /** Ejercicios de cada día de rutina, para detectar solapamiento muscular. */
  ejerciciosPorDia: Map<string, Ejercicio[]>;
  /** Todas las sesiones del usuario: las completadas cuentan como usadas. */
  sesiones: Sesion[];
  hoy: Fecha;
  /** Fuerza el punto de la rotación. Si falta, se deriva de la última sesión completada. */
  desdeRotacion?: number;
  umbralSolapamiento?: number;
}

/** Días bloqueados que aplican a este usuario y gimnasio (los de `user_id: null` son cierres). */
function fechasBloqueadas(e: EntradaMotor): Map<Fecha, string> {
  const out = new Map<Fecha, string>();
  for (const d of e.diasBloqueados) {
    if (d.gimnasio_id !== e.plan.gimnasio_id) continue;
    if (d.user_id !== null && d.user_id !== e.plan.user_id) continue;
    out.set(d.fecha, d.motivo);
  }
  return out;
}

function diaSemanaUTC(f: Fecha): number {
  return new Date(`${f}T00:00:00Z`).getUTCDay();
}

/** "15/08" — cómo se lee una fecha cuando va al lado de otra fecha. */
export function diaMes(f: Fecha): string {
  return `${f.slice(8, 10)}/${f.slice(5, 7)}`;
}

/**
 * "isquiotibiales y glúteos". Los músculos vienen ordenados por cuánto pesa el solapamiento, así
 * que los primeros son los que explican el conflicto; se nombran hasta tres y el resto se cuenta.
 *
 * En minúscula porque siempre va dentro de una frase: §4.4 pide que el recálculo sea explicable, y
 * un aviso que dice "encadenan Isquiotibiales" se lee como si fuera un nombre propio.
 */
function listarMusculos(ms: Musculo[], max = 3): string {
  const nombres = [...new Set(ms)].map((m) => nombreMusculo(m).toLowerCase());
  const visibles = nombres.slice(0, max);
  const resto = nombres.length - visibles.length;
  const frase =
    visibles.length <= 1
      ? visibles.join('')
      : `${visibles.slice(0, -1).join(', ')} y ${visibles[visibles.length - 1]!}`;
  return resto > 0 ? `${frase} (y ${resto} más)` : frase;
}

/**
 * Calcula el calendario propuesto y los números que lo acompañan.
 *
 * Asignación: se recorren los días hábiles en orden y se reparte la rotación del split.
 * Si dos días consecutivos cargarían el mismo músculo por encima del umbral (§6.3), se
 * intercala un día de descanso **si el colchón lo permite**; si no lo permite, se asigna
 * igual y se devuelve el conflicto marcado con la recomendación de §6.3. Nunca se salta
 * silenciosamente un día que el usuario necesita para no perder la sesión.
 */
export function calcularCalendario(e: EntradaMotor): PlanCalendario {
  const { plan, gimnasio, hoy } = e;
  const umbral = e.umbralSolapamiento ?? UMBRAL_SOLAPAMIENTO;

  const dias = [...e.rutinaDias].sort((a, b) => a.orden - b.orden);
  const bloqueadas = fechasBloqueadas(e);
  const cierres = new Set(gimnasio.dias_cierre_semanal);

  const sesionesDelPlan = e.sesiones.filter((s) => s.plan_id === plan.id);
  const usadas = sesionesDelPlan.filter((s) => s.estado === 'completada').length;
  const pendientesInicial = Math.max(0, plan.sesiones_totales - usadas);

  const ocupadas = new Set(
    e.sesiones.filter((s) => s.estado === 'completada' || s.estado === 'en_curso').map((s) => s.fecha),
  );

  const desde = hoy > plan.fecha_inicio ? hoy : plan.fecha_inicio;
  const hasta = plan.fecha_vencimiento;

  const noDisponibles: DiaNoDisponible[] = [];
  /** Días en que se puede entrenar. El orden importa: se asigna del más temprano al último. */
  const habiles: Fecha[] = [];

  for (const f of desde <= hasta ? rangoFechas(desde, hasta) : []) {
    // El orden de estos tres chequeos define que un día bloqueado que ADEMÁS cae en el
    // cierre semanal se cuente una sola vez. En el fixture el 23 de agosto es domingo y
    // está bloqueado: contarlo dos veces desajustaría el colchón.
    if (cierres.has(diaSemanaUTC(f))) {
      noDisponibles.push({ fecha: f, motivo: 'cierre_gimnasio', detalle: `${gimnasio.nombre} cerrado` });
      continue;
    }
    const motivo = bloqueadas.get(f);
    if (motivo !== undefined) {
      noDisponibles.push({ fecha: f, motivo: 'bloqueado', detalle: motivo });
      continue;
    }
    if (ocupadas.has(f)) {
      // El día ya se gastó: no es un día hábil disponible.
      noDisponibles.push({ fecha: f, motivo: 'ya_ocupado', detalle: 'Ya hay una sesión ese día' });
      continue;
    }
    habiles.push(f);
  }

  // Sesiones que el usuario ya dejó planificadas dentro del rango: son anclas, no huecos.
  const fijadas = new Map<Fecha, string>();
  for (const s of sesionesDelPlan) {
    if (s.estado !== 'planificada' || !s.rutina_dia_id) continue;
    if (s.fecha < desde || s.fecha > hasta) continue;
    if (!habiles.includes(s.fecha)) continue;
    fijadas.set(s.fecha, s.rutina_dia_id);
  }

  const diasHabiles = habiles.length;
  const colchon = diasHabiles - pendientesInicial;

  // Punto de partida de la rotación: la sesión completada más reciente define qué toca.
  const ultimaCompletada = [...e.sesiones]
    .filter((s) => s.estado === 'completada')
    .sort((a, b) => (a.fecha < b.fecha ? 1 : a.fecha > b.fecha ? -1 : 0))[0];

  let indice = 0;
  if (e.desdeRotacion !== undefined) {
    indice = ((e.desdeRotacion % Math.max(1, dias.length)) + dias.length) % Math.max(1, dias.length);
  } else if (ultimaCompletada?.rutina_dia_id) {
    const pos = dias.findIndex((d) => d.id === ultimaCompletada.rutina_dia_id);
    if (pos >= 0) indice = (pos + 1) % dias.length;
  }

  let anterior: { fecha: Fecha; rutina_dia_id: string } | null =
    ultimaCompletada?.rutina_dia_id
      ? { fecha: ultimaCompletada.fecha, rutina_dia_id: ultimaCompletada.rutina_dia_id }
      : null;

  const entradas: EntradaCalendario[] = [];
  let pendientes = pendientesInicial;
  let colchonRestante = Math.max(0, colchon);

  for (const fecha of habiles) {
    if (dias.length === 0) break;

    const fijada = fijadas.get(fecha);
    if (fijada !== undefined) {
      // Ancla: la fecha y el día de rutina los eligió el usuario. El conflicto se calcula
      // igual y se informa, pero no se mueve la sesión ni se intercala descanso.
      const pos = dias.findIndex((d) => d.id === fijada);
      const dia = pos >= 0 ? dias[pos]! : dias[indice % dias.length]!;
      entradas.push({
        fecha,
        rutina_dia_id: dia.id,
        nombre: dia.nombre,
        indiceRotacion: pos >= 0 ? pos : indice % dias.length,
        conflicto: detectarConflicto(fecha, dia, anterior, dias, e.ejerciciosPorDia, umbral),
        fijada: true,
      });
      anterior = { fecha, rutina_dia_id: dia.id };
      if (pos >= 0) indice = pos + 1;
      else indice++;
      pendientes--;
      continue;
    }

    if (pendientes === 0) break;

    const dia = dias[indice % dias.length]!;
    const conflicto = detectarConflicto(fecha, dia, anterior, dias, e.ejerciciosPorDia, umbral);

    if (conflicto && colchonRestante > 0) {
      // Se puede pagar un día de descanso sin arriesgar la sesión: se paga. Cuando el
      // colchón se agota se asigna igual, con el conflicto marcado: perder una sesión del
      // plan es peor que encadenar dos días, y la decisión es del usuario, no del motor.
      noDisponibles.push({
        fecha,
        motivo: 'descanso_recuperacion',
        detalle: `Descanso: ${listarMusculos(conflicto.musculos)} ${
          conflicto.musculos.length === 1 ? 'viene' : 'vienen'
        } de ayer`,
      });
      colchonRestante--;
      continue;
    }

    entradas.push({
      fecha,
      rutina_dia_id: dia.id,
      nombre: dia.nombre,
      indiceRotacion: indice % dias.length,
      conflicto,
      fijada: false,
    });
    anterior = { fecha, rutina_dia_id: dia.id };
    indice++;
    pendientes--;
  }

  const sesionesEnRiesgo = Math.max(0, pendientes);
  const ultimaFechaUtil = habiles.length > 0 ? habiles[habiles.length - 1]! : null;

  const avisos: AvisoCalendario[] = [];
  if (diffDias(hoy, plan.fecha_vencimiento) < 0) {
    avisos.push({ tipo: 'plan_vencido', texto: `El plan venció el ${plan.fecha_vencimiento}.` });
  }
  if (colchon < UMBRAL_COLCHON_CRITICO) {
    avisos.push({
      tipo: 'colchon_critico',
      texto:
        colchon < 0
          ? `No alcanzan los días: te faltan ${-colchon} para las ${pendientesInicial} sesiones pendientes.`
          : `Colchón de ${colchon} ${colchon === 1 ? 'día' : 'días'}: cualquier falta te deja sin margen.`,
    });
  }
  if (sesionesEnRiesgo > 0) {
    avisos.push({
      tipo: 'sesiones_en_riesgo',
      texto: `${sesionesEnRiesgo} ${sesionesEnRiesgo === 1 ? 'sesión' : 'sesiones'} van a sobrar al llegar al ${plan.fecha_vencimiento}.`,
    });
  }
  // §4.4 — "un recálculo que no se puede explicar es un recálculo en el que no se puede confiar":
  // el aviso nombra el músculo y los dos días. Sin el músculo, un día de hombros seguido de uno de
  // piernas parece un error del motor, cuando lo que los encadena son los isquios que el back
  // extension carga de secundario.
  for (const en of entradas) {
    if (!en.conflicto) continue;
    avisos.push({
      tipo: 'conflicto_recuperacion',
      texto: `Encadenan ${listarMusculos(en.conflicto.musculos)}: ${diaMes(en.conflicto.conFecha)} y ${diaMes(en.fecha)}. ${en.conflicto.recomendacion}`,
    });
  }

  return {
    desde,
    hasta,
    entradas,
    noDisponibles: noDisponibles.sort((a, b) => (a.fecha < b.fecha ? -1 : 1)),
    diasHabiles,
    sesionesUsadas: usadas,
    sesionesPendientes: pendientesInicial,
    colchon,
    colchonCritico: colchon < UMBRAL_COLCHON_CRITICO,
    sesionesEnRiesgo,
    ultimaFechaUtil,
    avisos,
  };
}

function detectarConflicto(
  fecha: Fecha,
  dia: RutinaDia,
  anterior: { fecha: Fecha; rutina_dia_id: string } | null,
  dias: RutinaDia[],
  ejerciciosPorDia: Map<string, Ejercicio[]>,
  umbral: number,
): ConflictoRecuperacion | null {
  if (!anterior) return null;
  if (diffDias(anterior.fecha, fecha) !== 1) return null;

  const ejsHoy = ejerciciosPorDia.get(dia.id) ?? [];
  const ejsAyer = ejerciciosPorDia.get(anterior.rutina_dia_id) ?? [];
  if (ejsHoy.length === 0 || ejsAyer.length === 0) return null;

  const { score, musculos } = solapamientoEntreDias(ejsHoy, ejsAyer);
  if (score < umbral) return null;

  // El ejercicio a adelantar es aquel para el que el músculo cansado es el OBJETIVO, no uno que lo
  // roza de secundario: con [isquios] entre el Día 2 y el Día 3, recomendar el leg press —que ya
  // es el primero de la lista y sólo los usa de acompañantes— sonaría a que el motor no miró la
  // rutina. Se cae al secundario sólo si ningún ejercicio del día lo tiene de primario.
  const afectado = musculos[0];
  const ejercicioAfectado = afectado
    ? ejsHoy.find((x) => x.musculo_primario === afectado) ??
      ejsHoy.find((x) => x.musculos_secundarios.includes(afectado))
    : undefined;

  const nombreDiaAnterior = dias.find((d) => d.id === anterior.rutina_dia_id)?.nombre ?? 'el día anterior';

  return {
    conFecha: anterior.fecha,
    musculos,
    score,
    recomendacion: ejercicioAfectado
      ? `Reordená la sesión y poné ${ejercicioAfectado.nombre} primero, con el músculo fresco (viene de ${nombreDiaAnterior}).`
      : `Reordená la sesión para poner primero el ejercicio afectado (viene de ${nombreDiaAnterior}).`,
  };
}

// ---------------------------------------------------------------------------
// Explicabilidad (§4.4: "el recálculo debe ser incremental y explicable")
// ---------------------------------------------------------------------------

export type TipoCambio =
  | 'colchon'
  | 'dia_perdido'
  | 'dia_recuperado'
  | 'sesion_movida'
  | 'sesiones_en_riesgo'
  | 'conflicto';

export interface CambioCalendario {
  tipo: TipoCambio;
  texto: string;
}

const NOMBRES_DIA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

export function nombreDia(f: Fecha): string {
  return NOMBRES_DIA[diaSemanaUTC(f)] ?? f;
}

/** "jueves 14 de agosto" */
export function fechaLarga(f: Fecha): string {
  const meses = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ];
  const dia = Number(f.slice(8, 10));
  const mes = meses[Number(f.slice(5, 7)) - 1] ?? '';
  return `${nombreDia(f)} ${dia} de ${mes}`;
}

/**
 * Traduce dos calendarios a las frases que la app le muestra al usuario, del tipo
 * "perdiste el jueves → el colchón pasó de 4 a 3 días" (§4.4).
 */
export function explicarCambios(antes: PlanCalendario, despues: PlanCalendario): CambioCalendario[] {
  const cambios: CambioCalendario[] = [];

  const habilesAntes = new Set(
    antes.entradas.map((x) => x.fecha).concat(
      antes.noDisponibles.filter((d) => d.motivo === 'descanso_recuperacion').map((d) => d.fecha),
    ),
  );
  const habilesDespues = new Set(
    despues.entradas.map((x) => x.fecha).concat(
      despues.noDisponibles.filter((d) => d.motivo === 'descanso_recuperacion').map((d) => d.fecha),
    ),
  );

  for (const d of despues.noDisponibles) {
    if (d.motivo === 'descanso_recuperacion' || d.motivo === 'cierre_gimnasio') continue;
    if (habilesAntes.has(d.fecha) && !habilesDespues.has(d.fecha)) {
      cambios.push({ tipo: 'dia_perdido', texto: `Perdiste el ${fechaLarga(d.fecha)}: ${d.detalle}.` });
    }
  }
  for (const d of antes.noDisponibles) {
    if (d.motivo === 'descanso_recuperacion' || d.motivo === 'cierre_gimnasio') continue;
    if (!habilesAntes.has(d.fecha) && habilesDespues.has(d.fecha)) {
      cambios.push({ tipo: 'dia_recuperado', texto: `Se liberó el ${fechaLarga(d.fecha)}.` });
    }
  }

  if (antes.colchon !== despues.colchon) {
    cambios.push({
      tipo: 'colchon',
      texto: `El colchón pasó de ${antes.colchon} a ${despues.colchon} ${Math.abs(despues.colchon) === 1 ? 'día' : 'días'}.`,
    });
  }

  const primeraAntes = antes.entradas[0];
  const primeraDespues = despues.entradas[0];
  if (primeraAntes && primeraDespues && primeraAntes.fecha !== primeraDespues.fecha) {
    cambios.push({
      tipo: 'sesion_movida',
      texto: `La próxima sesión (${primeraDespues.nombre}) se movió del ${fechaLarga(primeraAntes.fecha)} al ${fechaLarga(primeraDespues.fecha)}.`,
    });
  }

  if (antes.sesionesEnRiesgo !== despues.sesionesEnRiesgo) {
    cambios.push({
      tipo: 'sesiones_en_riesgo',
      texto:
        despues.sesionesEnRiesgo > antes.sesionesEnRiesgo
          ? `Ahora hay ${despues.sesionesEnRiesgo} ${despues.sesionesEnRiesgo === 1 ? 'sesión' : 'sesiones'} en riesgo de vencer (antes ${antes.sesionesEnRiesgo}).`
          : `Bajaron las sesiones en riesgo: de ${antes.sesionesEnRiesgo} a ${despues.sesionesEnRiesgo}.`,
    });
  }

  // "El mismo músculo" no explica nada: hay que poder decir CUÁL y en qué días, o el usuario no
  // tiene con qué decidir si le importa (§4.4).
  const fechasEnConflictoAntes = new Set(
    antes.entradas.filter((x) => x.conflicto).map((x) => x.fecha),
  );
  const conflictosAntes = fechasEnConflictoAntes.size;
  const conflictosDespues = despues.entradas.filter((x) => x.conflicto).length;
  const nuevos = despues.entradas.filter((x) => x.conflicto && !fechasEnConflictoAntes.has(x.fecha));
  if (conflictosDespues > conflictosAntes && nuevos.length > 0) {
    cambios.push({
      tipo: 'conflicto',
      texto: `El nuevo orden encadena ${listarMusculos(
        nuevos.flatMap((x) => x.conflicto!.musculos),
      )} con el día anterior: ${nuevos.map((x) => diaMes(x.fecha)).join(', ')}.`,
    });
  }

  return cambios;
}

/** La próxima sesión propuesta. Para la home y para arrancar el registro. */
export function proximaSesion(cal: PlanCalendario): EntradaCalendario | null {
  return cal.entradas[0] ?? null;
}

/** Simula perder un día: sirve para "¿qué pasa si no voy mañana?". */
export function simularBloqueo(e: EntradaMotor, fecha: Fecha, motivo = 'simulación'): {
  antes: PlanCalendario;
  despues: PlanCalendario;
  cambios: CambioCalendario[];
} {
  const antes = calcularCalendario(e);
  const bloqueoSimulado: DiaBloqueado = {
    id: `sim-${fecha}`,
    user_id: e.plan.user_id,
    gimnasio_id: e.plan.gimnasio_id,
    fecha,
    motivo,
  };
  const despues = calcularCalendario({ ...e, diasBloqueados: [...e.diasBloqueados, bloqueoSimulado] });
  return { antes, despues, cambios: explicarCambios(antes, despues) };
}

/** Rango de fechas de un viaje: bloquea desde `desde` hasta `hasta`, ambos inclusive. */
export function bloquearRango(
  userId: string,
  gimnasioId: string,
  desde: Fecha,
  hasta: Fecha,
  motivo: string,
): DiaBloqueado[] {
  return rangoFechas(desde, hasta).map((fecha) => ({
    id: `${userId}-${fecha}`,
    user_id: userId,
    gimnasio_id: gimnasioId,
    fecha,
    motivo,
  }));
}
